import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ToastContent from "~@/components/layout/ToastContent";
import {
  DealerExportKind,
  useRequestDealerExportMutation,
} from "~@/graphql/codegen/generated";

// One export toast at a time across every export button. ToastContainer uses
// limit={1}, so without this each extra click queued another toast that
// played after the previous one. Cleared when the toast closes.
let exportToastOpen = false;
// Longer than the default 5s autoClose plus a queued wait behind one other toast.
const EXPORT_TOAST_GUARD_MS = 15000;

// [AI_START::function id="showExportToast" purpose="Show the single export toast and remember it until it closes, queued or visible" links=["useDealerExport"]]
function showExportToast(
  type: "success" | "warning" | "error",
  message: string
): void {
  exportToastOpen = true;
  // Safety net: a toast dropped from the queue never fires onClose.
  const release = window.setTimeout(() => {
    exportToastOpen = false;
  }, EXPORT_TOAST_GUARD_MS);
  toast(<ToastContent type={type} message={message} />, {
    onClose: () => {
      window.clearTimeout(release);
      exportToastOpen = false;
    },
  });
}
// [AI_END::showExportToast]

// [AI_START::function id="useDealerExport" purpose="Queue one export per click; clicks are ignored while a request is in flight or an export toast is still showing; returns the server status so a modal can close on success" links=["useRequestDealerExportMutation", "showExportToast"]]
export default function useDealerExport() {
  const { t } = useTranslation();
  const [request, { loading }] = useRequestDealerExportMutation();
  const inFlight = useRef(false);
  const [busy, setBusy] = useState(false);

  const requestExport = useCallback(
    async (
      kind: DealerExportKind,
      range?: {
        dateStart?: string | null;
        dateEnd?: string | null;
        offsetMinutes?: number | null;
      }
    ): Promise<string | null> => {
      if (inFlight.current || exportToastOpen) {
        return null;
      }
      inFlight.current = true;
      setBusy(true);
      try {
        const { data } = await request({
          variables: {
            kind,
            dateStart: range?.dateStart ?? null,
            dateEnd: range?.dateEnd ?? null,
            offsetMinutes: range?.offsetMinutes ?? null,
          },
        });
        const result = data?.requestDealerExport;
        const email = result?.email || t("export.organization-email");
        if (result?.status === "already_preparing") {
          showExportToast("warning", t("export.already-preparing"));
          return result.status;
        }
        if (result?.status === "missing_email") {
          showExportToast("error", t("export.missing-email"));
          return result.status;
        }
        if (result?.status !== "queued") {
          showExportToast("error", t("export.failed"));
          return result?.status ?? null;
        }
        showExportToast("success", t("export.link-sent", { email }));
        return result.status;
      } catch {
        showExportToast("error", t("export.failed"));
        return null;
      } finally {
        inFlight.current = false;
        setBusy(false);
      }
    },
    [request, t]
  );

  return { requestExport, loading: busy || loading };
}
// [AI_END::useDealerExport]
